import { ca } from "date-fns/locale";
import moment from "moment";
import "moment-timezone";

export const convertDate = (date, options = {}) => {
  if (!(date instanceof Date)) {
    return "null";
  }
  const cstOptions = { timeZone: "America/Chicago" };

  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
    ...cstOptions,
    ...options,
  });
};

// Timezone of a reservation/space payload (reservation snapshot first, then
// space, then the default) — mirrors the API's resolveReservationTimezone.
export const getReservationTimezone = (reservation) => {
  const candidates = [reservation?.timezone, reservation?.Space?.timezone];
  return candidates.find((candidate) => candidate && moment.tz.zone(candidate)) || "America/Chicago";
};

// Reservation start/end instant as wall-clock "DD MMM, YYYY h:mm A" in the
// reservation/space timezone, so Admin shows the same time-of-day the renter
// selected (move-in time) rather than a browser-local or date-only reading.
// Reservation timestamps are formatted as wall-clock time in the space's
// timezone. Legacy date-only starts are backfilled as local midnight.
export const getReservationDateTime = (dateTime, reservation) => {
  if (!dateTime) return null;
  const m = moment(dateTime).tz(getReservationTimezone(reservation));
  if (!m.isValid()) return null;
  return m.format("DD MMM, YYYY h:mm A");
};

// get date
export const getDate = (dateTime) => {
  if (!dateTime) return null;
  const date = moment(dateTime.split("T")[0]).format("DD MMM, YYYY");
  // console.log('moment', moment.('America/Chicago').startOf('day'));
  return date;
};

export const getDateTime = (dateTime) => {
  if (!dateTime) return null;
  const date = moment(dateTime).format("DD MMM, YYYY HH:mm:ss");
  // console.log('moment', moment.('America/Chicago').startOf('day'));
  return date;
};

/**
 * Converts Stripe amount (in smallest currency unit) to a normal amount.
 * @param {number} amount - The amount in the smallest currency unit (e.g., cents).
 * @param {string} currency - The currency code (default is 'usd').
 * @returns {number} - The normal amount.
 */
export function convertStripeAmount(amount) {
  // Define conversion rates for different currencies if necessary
  // For simplicity, we're assuming the currency has two decimal places
  const conversionRate = 100;

  if (amount <= 0) {
    return 0;
  }

  // Perform the conversion
  return amount / conversionRate;
}

export const getReservationTypeLabel = (reservationType) => {
  const type = Number(reservationType);
  switch (type) {
    case 0:
      return "Weekly";
    case 1:
      return "Monthly";
    case 2:
      return "Daily";
    case 3:
      return "1 Month";
    default:
      return "N/A";
  }
};

export const getRentelRateByType = (reservationType, space) => {
  const type = Number(reservationType);
  switch (type) {
    case 0:
      return space?.weeklyRate;
    case 1:
      return space?.monthlyRate;
    case 2:
      return space?.dailyRate;
    case 3:
      return space?.monthlyRate;
    default:
      return 0;
  }
};

export const hasRepeatDiscount = (paymentDetails = null) => {
  if (!paymentDetails) return false;

  return !!(
    paymentDetails?.repeatDiscountRate > 0 &&
    paymentDetails?.repeatDiscountPrice > 0
  );
};

 export const getSpaceAdddress = (space) => {
    let address = "";

    if (space?.addressOne) {
      address += space.addressOne;
    }
    if (space?.addressTwo) {
      address += address ? `, ${space.addressTwo}` : space.addressTwo;
    }
    if (space?.city) {
      address += address ? `, ${space.city}` : space.city;
    }
    if (space?.stateCode) {
      address += address ? `, ${space.stateCode}` : space.stateCode;
    }
    if (space?.zipCode) {
      address += address ? `, ${space.zipCode}` : space.zipCode;
    }

    return address;
};

export const getRentelUniPrice = (reservation) => {
  const type = Number(reservation?.reservationType);
  const rate = reservation?.rate ?? 0;
  const quantity = reservation?.quantity ?? 1;

  switch (type) {
    case 0: { // Weekly
      const weeklyDuration = reservation?.weeklyBookingDuration ?? 1;
      return rate / weeklyDuration / quantity;
    }
    case 1: // Monthly
      return rate / quantity;

    case 2: { // Daily
      const reservationDays = reservation?.reservationDays ?? 1;
      return rate / quantity / reservationDays;
    }
    case 3: // 1 Month
      return rate / quantity;
    default:
      return 0;
  }
};

export const getUserFullName = (user) => {
  
  let name = "";
  
  if (user?.firstName) {
    name += user.firstName;
  }
  if (user?.lastName) {
    name += name ? ` ${user.lastName}` : user.lastName;
  }

  return name;
}

export const formatNumberWithCommas = (number) => {
  if (typeof number !== "number") {
    return "$0.00";
  }
  return number.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

// using moment js
export const getCurrentDateTimeInUTC = () => {
  return moment.utc().format("YYYY-MM-DD_HH-mm-ssZ");
}

// get datetime in cst timezone
export const getDateTimeInCST = () => {
  return moment().utc().format("YYYY-MM-DD_HH-mm-ssZ");
};

/**
 * Validate that a vehicle fits within a space.
 *
 * The vehicle carries a single `size` value (in feet). It is compared against
 * the space's `spaceLength` — if the size exceeds it the vehicle does not fit.
 * A space length that is empty / 0 is treated as "no constraint" and skipped.
 *
 * @param {object} space   - { spaceLength }
 * @param {object} vehicle - { size }
 * @returns {{ valid: boolean, errors: string[] }}
 */
export const validateVehicleSize = (space, vehicle) => {
  const errors = [];
  if (!space || !vehicle) return { valid: true, errors };

  const toNum = (val) => {
    const n = parseFloat(val);
    return Number.isFinite(n) && n > 0 ? n : null;
  };

  const size = toNum(vehicle.size);
  const spaceLength = toNum(space.spaceLength);

  if (size !== null && spaceLength !== null && size > spaceLength) {
    errors.push(`Vehicle size (${size}ft) exceeds space length (${spaceLength}ft).`);
  }

  return { valid: errors.length === 0, errors };
};

export const getReservationCalendarDate = (dateTime, reservation) =>
  dateTime ? moment.utc(dateTime).tz(getReservationTimezone(reservation)).format("YYYY-MM-DD") : null;

export const getReservationDateInput = (dateTime, reservation) =>
  dateTime ? moment.utc(dateTime).tz(getReservationTimezone(reservation)).format("YYYY-MM-DD") : "";

export const getReservationDateTimeInput = (dateTime, reservation) =>
  dateTime ? moment.utc(dateTime).tz(getReservationTimezone(reservation)).format("YYYY-MM-DDTHH:mm") : "";

export const getTodayInReservationTimezone = (reservation) =>
  moment().tz(getReservationTimezone(reservation)).format("YYYY-MM-DD");

export const getMinimumBookingDateInTimezone = (timezone = "America/Chicago", daysBeforeBooking = 0) => {
  const safeTimezone = moment.tz.zone(timezone) ? timezone : "America/Chicago";
  const days = Math.max(0, Number(daysBeforeBooking) || 0);
  return moment().tz(safeTimezone).add(days, "days").format("YYYY-MM-DD");
};

export const isWallClockInPast = (date, time = "00:00", timezone = "America/Chicago") => {
  if (!date) return false;
  const selected = moment.tz(`${date}T${time || "00:00"}:00`, "YYYY-MM-DDTHH:mm:ss", true, timezone || "America/Chicago");
  return !selected.isValid() || selected.format("YYYY-MM-DDTHH:mm:ss") !== `${date}T${time || "00:00"}:00` || selected.isBefore(moment());
};
